import { isDevelopment } from "@settings";

/**
 * The `xorCipher` function in TypeScript performs XOR encryption on a given string
 * using a specified key.
 * @param {string} str - The `str` parameter is the input string that you want to
 * encrypt using the XOR cipher algorithm.
 * @param {string} [key] - The `key` parameter in the `xorCipher` function is an
 * optional parameter that represents the key used for XOR encryption. It is a
 * string that is used to XOR each character of the input string during encryption
 * or decryption. If the `key` parameter is not provided, an error will be thrown
 * @returns The function `xorCipher` returns a string that is the result of XOR
 * cipher encryption on the input string `str` using the provided `key`. If the
 * `isDevelopment` flag is set to true, it simply returns the input string without
 * encryption. If the `key` is not provided, it throws an error.
 */
function xorCipher(str: string, key?: string): string {
  if (isDevelopment) return str;
  if (key === undefined) throw new Error("key is undefined");
  let output = "";
  for (let i = 0; i < str.length; i++) {
    output += String.fromCharCode(
      str.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }
  return output;
}

/**
 * The `encodeXorCipher` function in TypeScript encodes a given string using a XOR
 * cipher with a specified key.
 * @param {string} str - The `str` parameter is the string that you want to encode
 * using the XOR cipher algorithm.
 * @param {string} key - The key parameter is a string that is used in the XOR
 * cipher algorithm to encode or decode the input string. It is essentially a
 * secret key that determines how the characters in the input string are encrypted
 * or decrypted.
 * @returns The `encodeXorCipher` function is returning the result of the
 * `xorCipher` function applied to the input `str` and `key` parameters.
 */
export function encodeXorCipher(str: string, key: string) {
  return xorCipher(str, key);
}

/**
 * The function `decodeXorCipher` in TypeScript decodes a XOR cipher using a given
 * key.
 * @param {string} str - The `str` parameter is the string that you want to decode
 * using the XOR cipher algorithm.
 * @param {string} key - The key parameter in the XOR cipher function is a string
 * that is used to XOR (exclusive OR) with the input string to encrypt or decrypt
 * the data. It is essentially a secret key that determines how the encryption or
 * decryption process is performed.
 * @returns The `decodeXorCipher` function is returning the result of the
 * `xorCipher` function applied to the input string `str` and key `key`.
 */
export function decodeXorCipher(str: string, key: string) {
  return xorCipher(str, key);
}
